import RefLink from '../shared/RefLink';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import { inputCls } from '../shared/FormKit';
import { formatDateShort } from '../../utils/dateFormat';
import { engagementTag } from '../../utils/engagementName';

// 同公司同 FYE 可以有審計案與 AUP 案並存（打勾的服務還會編號）——每列除了
// 公司名，再印服務（＋序號）與可多案服務的描述第一行（engagementTag），
// 否則分不出 AUP 與 AUP #2。
// 分組只到年份，同一組裡的公司 FYE 日期可能不同，所以每列再印自己的 FYE
// ——標頭只講年份，日期要在列上看得到。
// 名字與這行都完整顯示、過長換行不截斷——有客戶名只差最後幾個字。
const ServiceMeta = ({ e, className = 'text-[11px] text-slate-400' }) => {
  const parts = [engagementTag(e.serviceProvided, e.engagementSeq, e.serviceDescription)].filter(Boolean);
  if (e.yearEnd) parts.push(`FYE ${formatDateShort(e.yearEnd)}`);
  if (parts.length === 0) return null;
  return (
    <span className={`${className} break-words`}>
      {parts.join(' · ')}
    </span>
  );
};

// 涵蓋選單——QC／FS／QC35 的 New 與 Modify、三張修改單、CWCC、
// Management 三種記錄表單與涵蓋編輯**全部共用這一個**，勿另寫選單。
//
// 行為：下拉本身就以「集團 · FY 年份」分組列出公司，點了第一間之後選項立即
// 縮到同集團＋FYE 同年——不用先選完再等下面跳出加選區。專案的定義同看板：
// 集團＋FYE 年份（日期可不同），跨 ENR 也算同一個專案——與後端
// utils/projectKey.js 同一把尺。
//
// Props：
//   engagements  — slimList 列（id, engagementName, entityName, clientGroup,
//                  yearEnd, sourceRequestNo, partners…），呼叫端已先過濾
//                  （成員／尚未涵蓋）。
//   selectedIds  — number[]
//   onChange     — (ids) => void
//   renderExtra  — 選配 (engagement) => node，附掛在已選清單每列之後
//   lockedIds    — 不可移除的案（涵蓋可加不可減）；有鎖時 Clear 也不出現
//   scope        — 'project'（預設）＝選了第一間後只列同集團、FYE 同年；
//                  'all'＝不縮選（三張修改單與記錄涵蓋編輯：伺服器已把 feed
//                  篩到該記錄的集團，姊妹公司可能較晚登記、FYE 也可能不同，
//                  縮選會把該給的選項藏掉）。分組標頭兩種模式都有。
//   single       — 單選模式（EAR New／ESC New／Signed Financial Statements／Management 的
//                  Engagement Status 與 FS 記錄共用）：點一間＝整組取代並收合
//                  下拉、不縮選（換案本來就要看得到全部專案）。selectedIds 仍是
//                  陣列，呼叫端 `ids[0]` 取值；legacy 資料帶進多個已選時逐一按
//                  X 可各自移除——同一顆元件兩種模式，不再長第二份選單。
const fyeYearOf = (yearEnd) => (yearEnd ? String(yearEnd).slice(0, 4) : '');
const fyeLabel = (yearEnd) => `FY ${fyeYearOf(yearEnd)}`;
const keyOf = (e) => `${e.clientGroup || e.entityName || e.engagementName || ''}|${fyeYearOf(e.yearEnd)}`;

// Reference links on a selected company's chip (owner 2026-10-15), both in a
// new tab (RefLink): the company's Client Hub page, and "last year's
// engagement" — the same company's earlier engagement that already has a
// QC6 / QC7, opened in My Records (the engagement page there carries the
// whole questionnaire; the link lands on the engagement, not on a QC page). The feed decides
// what is offered: `clientHubHref` only for callers holding the hub key,
// `priorQc.inMyRecords` only when the caller is a member of that engagement.
const EngagementRefLinks = ({ e }) => {
  const prior = e.priorQc && e.priorQc.inMyRecords ? e.priorQc : null;
  if (!e.clientHubHref && !prior) return null;
  const cls = 'text-[10px] text-blue-600 hover:underline whitespace-nowrap';
  return (
    <>
      {e.clientHubHref && (
        <RefLink to={e.clientHubHref} className={cls} title="Open this company's Client Hub page in a new tab">Client Hub</RefLink>
      )}
      {prior && (
        <RefLink
          to={`/my/engagements/${prior.id}`}
          className={cls}
          title={`The ${fyeLabel(prior.yearEnd)} engagement, with its ${prior.qcType || 'QC6 / QC7'} — opens in My Records in a new tab`}
        >
          Last year's engagement
        </RefLink>
      )}
    </>
  );
};

const GroupedEngagementPicker = ({
  engagements, selectedIds, onChange, renderExtra = null, lockedIds = [], scope = 'project', single = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const boxRef = useRef(null);
  const locked = useMemo(() => new Set((lockedIds || []).map(Number)), [lockedIds]);

  const selected = useMemo(
    () => engagements.filter((e) => selectedIds.includes(e.id)),
    [engagements, selectedIds]
  );
  // 選了第一間，專案就定了——之後只列同集團、FYE 同年的公司（scope='all' 與
  // 單選模式不縮：換案要看得到全部專案）。
  const activeKey = (scope === 'all' || single) ? null : (selected.length > 0 ? keyOf(selected[0]) : null);
  const active = selected[0] || null;

  useEffect(() => {
    const onDoc = (ev) => {
      if (boxRef.current && !boxRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // 依 feed 順序（slimList＝FYE 新到舊）分組；搜尋比集團／公司／案名。
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const map = new Map();
    for (const e of engagements) {
      const k = keyOf(e);
      if (activeKey && k !== activeKey) continue;
      if (q) {
        const hay = `${e.clientGroup || ''} ${e.entityName || ''} ${e.engagementName || ''} ${e.serviceProvided || ''} ${e.descriptionOfService || ''}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      if (!map.has(k)) {
        map.set(k, { key: k, clientGroup: e.clientGroup || e.entityName, yearEnd: e.yearEnd, items: [] });
      }
      map.get(k).items.push(e);
    }
    return [...map.values()];
  }, [engagements, activeKey, search]);

  const toggle = (e) => {
    if (locked.has(e.id)) return;
    if (single) {
      // 單選：點一間＝整組取代並收合；點已選的一間＝只取消那一間
      // （legacy 多選資料才移除得掉個別項）。
      const next = selectedIds.includes(e.id)
        ? selectedIds.filter((x) => x !== e.id)
        : [e.id];
      onChange(next);
      if (!selectedIds.includes(e.id)) { setOpen(false); setSearch(''); }
      return;
    }
    onChange(selectedIds.includes(e.id)
      ? selectedIds.filter((x) => x !== e.id)
      : [...selectedIds, e.id]);
  };
  const clearAll = () => onChange([]);

  return (
    <div ref={boxRef} className="space-y-3">
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(ev) => { setSearch(ev.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={activeKey
            ? `Add another ${active.clientGroup || ''} company with FYE in ${fyeYearOf(active.yearEnd)}…`
            : (single && selected.length > 0
              ? 'Search to change the engagement…'
              : 'Search client group, company or engagement…')}
          className={`${inputCls} pl-9`}
        />
        {open && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
            {groups.length === 0 ? (
              <p className="px-3 py-3 text-sm text-slate-500">
                {activeKey
                  ? 'No other company of this client group and FYE year is available.'
                  : 'No matching engagement.'}
              </p>
            ) : groups.map((g) => (
              <div key={g.key}>
                {/* 集團 · FY 年份當作資料夾標頭，公司收在底下——這就是「專案」。 */}
                <div className="sticky top-0 px-3 py-1.5 bg-slate-50 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {g.clientGroup} · {fyeLabel(g.yearEnd)}
                </div>
                {g.items.map((e) => {
                  const on = selectedIds.includes(e.id);
                  const frozen = locked.has(e.id);
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => toggle(e)}
                      disabled={frozen}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm ${
                        frozen ? 'text-slate-400 cursor-not-allowed' : 'hover:bg-blue-50/60 text-slate-700'
                      }`}
                    >
                      <span className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center ${
                        on ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'
                      }`}>
                        {on && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span className="min-w-0 flex-1 flex flex-col">
                        <span className="break-words">{e.entityName || e.engagementName}</span>
                        <ServiceMeta e={e} />
                      </span>
                      {e.sourceRequestNo && (
                        <span className="text-[11px] font-mono text-slate-400 shrink-0">{e.sourceRequestNo}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span className="font-semibold text-slate-600">{active.clientGroup || active.entityName}</span>
            {' · '}{fyeLabel(active.yearEnd)}
            {!single && <>{' · '}{selected.length} {selected.length === 1 ? 'company' : 'companies'} selected</>}
            {locked.size === 0 && (
              <button type="button" onClick={clearAll} className="ml-auto text-xs text-slate-500 hover:text-slate-700 underline">
                Clear
              </button>
            )}
          </div>
          {/* 每家固定兩行：公司名＋移除鈕／服務、FYE 與連結——長短不一的公司名
              不會讓有的列一行、有的列兩行。 */}
          <div className="space-y-1.5">
            {selected.map((e) => {
              const frozen = locked.has(e.id);
              return (
                <div key={e.id} className="flex items-start gap-2 pl-2.5 pr-1.5 py-1 rounded-lg bg-white border border-slate-200 text-xs text-slate-700">
                  <div className="min-w-0 flex-1">
                    <div className="break-words">{e.entityName || e.engagementName}</div>
                    <div className="flex flex-wrap items-center gap-x-1.5">
                      <ServiceMeta e={e} className="text-[10px] text-slate-400" />
                      {e.sourceRequestNo && <span className="font-mono text-[10px] text-slate-400">{e.sourceRequestNo}</span>}
                      <EngagementRefLinks e={e} />
                    </div>
                  </div>
                  {frozen ? (
                    <span className="shrink-0 px-1 text-[10px] text-slate-400">locked</span>
                  ) : (
                    <button type="button" onClick={() => toggle(e)} className="shrink-0 p-0.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600" title="Remove">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {renderExtra && (
            <div className="space-y-1.5 pt-1">
              {selected.map((e) => {
                const extra = renderExtra(e);
                return extra ? <div key={e.id}>{extra}</div> : null;
              })}
            </div>
          )}
          {scope !== 'all' && !single && (
            <p className="text-[11px] text-slate-400">
              One submission covers companies of the same client group whose FYE falls in the same year — clear the selection to switch project.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupedEngagementPicker;
